import { z } from 'zod';

import { pymixApiClient } from '/@/renderer/api/pymix/pymix-api';
import {
    DjHistory,
    DjPatch,
    DjSummary,
    DjTrack,
    PlaylistTree,
    pymixType,
    UpdatePlaylistNodeBody,
    UserSettings,
} from '/@/shared/api/pymix/pymix-types';

type CreateArgs = {
    body: z.infer<typeof pymixType._parameters.create>;
};

type DeleteSongArgs = {
    body: z.infer<typeof pymixType._parameters.deleteSong>;
};

type DownloadFileArgs = {
    filename: string;
    responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'stream' | 'text';
};

type ImportArgs = {
    body: z.infer<typeof pymixType._parameters.import>;
};

type ImportProgressArgs = {
    query: z.infer<typeof pymixType._parameters.importProgress>;
};

type InviteRequestArgs = {
    body: z.infer<typeof pymixType._parameters.inviteRequest>;
};

/**
 * Thrown when pymix refuses a write because the caller can't upload — today that means
 * the public `demo` login hitting `require_uploader`.
 *
 * A distinct type rather than a message match: the UI's job here is to say "locked", not
 * "broken", and a generic Error would leave every call site guessing from a string.
 */
export class PymixUploadForbiddenError extends Error {
    constructor(action: string) {
        super(`This account cannot ${action}`);
        this.name = 'PymixUploadForbiddenError';
    }
}

export const isUploadForbidden = (err: unknown): boolean =>
    err instanceof PymixUploadForbiddenError;

export type DjWriteResult =
    | { conflict: false; track: DjTrack }
    | { conflict: true; current: DjTrack };

/** Why an invite request failed, so the form can say something useful about each case. */
export type InviteRequestFailure = 'failed' | 'invalid' | 'rateLimited';

type LoginArgs = {
    body: z.infer<typeof pymixType._parameters.login>;
};

type MatchTracksArgs = {
    body: z.infer<typeof pymixType._parameters.matchTracks>;
};

type PymixClientArgs = {
    baseUrl: string;
    signal?: AbortSignal;
    token?: string;
};

type RbDownloadArgs = {
    body: z.infer<typeof pymixType._parameters.exportJob>;
};

type RbImportArgs = {
    body: z.infer<typeof pymixType._parameters.rbImport>;
};

type SeratoExportArgs = {
    body: z.infer<typeof pymixType._parameters.seratoExport>;
};

type SeratoImportArgs = {
    body: z.infer<typeof pymixType._parameters.seratoImport>;
};

type StorageCheckArgs = {
    query: z.infer<typeof pymixType._parameters.storageCheck>;
};

type SyncArgs = {
    body: z.infer<typeof pymixType._parameters.sync>;
};

type SyncPlanArgs = {
    body: z.infer<typeof pymixType._parameters.syncPlan>;
};

type SyncPlaylistsArgs = {
    body: z.infer<typeof pymixType._parameters.syncPlaylists>;
};

type SyncTracksArgs = {
    body: z.infer<typeof pymixType._parameters.syncTracks>;
};

type ValidateTokenArgs = {
    query: z.infer<typeof pymixType._parameters.isValidToken>;
};

type WishlistBulkCreateArgs = {
    body: z.infer<typeof pymixType._parameters.wishlistBulkCreate>;
};

type WishlistCreateArgs = {
    body: z.infer<typeof pymixType._parameters.wishlistCreate>;
};

type WishlistDeleteArgs = {
    params: { id: string };
};

type WishlistMatchMetadataArgs = {
    body: z.infer<typeof pymixType._parameters.wishlistMatchMetadata>;
};

type WishlistMatchYoutubeArgs = {
    params: { id: string };
};

type WishlistParseLinkArgs = {
    body: z.infer<typeof pymixType._parameters.wishlistParseLink>;
};

type WishlistSetSheetArgs = {
    body: z.infer<typeof pymixType._parameters.wishlistSetSheet>;
};

type WishlistUpdateArgs = {
    body: z.infer<typeof pymixType._parameters.wishlistUpdate>;
};

export class PymixInviteRequestError extends Error {
    readonly reason: InviteRequestFailure;

    constructor(reason: InviteRequestFailure) {
        super(`Invite request failed: ${reason}`);
        this.name = 'PymixInviteRequestError';
        this.reason = reason;
    }
}

const djWriteResult = (res: { body: unknown; status: number }, fallback: string): DjWriteResult => {
    const data = (res.body as null | { data?: unknown })?.data;
    if (res.status === 200) return { conflict: false, track: data as DjTrack };
    if (res.status === 409)
        return { conflict: true, current: (data as { current: DjTrack }).current };
    const detail = (data as null | { detail?: unknown })?.detail;
    throw new Error(typeof detail === 'string' ? detail : fallback);
};

export const PymixController = {
    checkStorage: async (args: PymixClientArgs & StorageCheckArgs) => {
        const { baseUrl, query, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).storageCheck({ query });

        if (res.status !== 200) {
            throw new Error('Failed to check storage');
        }

        return res.body.data;
    },

    create: async (args: CreateArgs & PymixClientArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).create({ body });

        if (res.status !== 200) {
            throw new Error('Failed to create user');
        }

        return res.body.data;
    },

    // The tree's write routes (#148). A refusal (400 bad move, 404 node gone, 409 no
    // tree) throws: the caller rolls back and the next tree read shows the truth.
    createPlaylistFolder: async (
        args: PymixClientArgs & {
            body: z.infer<typeof pymixType._parameters.createPlaylistFolder>;
        },
    ) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).createPlaylistFolder({
            body,
        });

        if (res.status !== 200) {
            throw new Error('Failed to create folder');
        }

        return res.body.data;
    },

    createPlaylistInFolder: async (
        args: PymixClientArgs & {
            body: z.infer<typeof pymixType._parameters.createPlaylistInFolder>;
        },
    ) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).createPlaylistInFolder({
            body,
        });

        if (res.status !== 200) {
            throw new Error('Failed to create playlist');
        }

        return res.body.data;
    },

    deleteDuplicates: async (args: PymixClientArgs) => {
        const { baseUrl, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).deleteDuplicates();

        if (res.status !== 200) {
            throw new Error('Failed to delete duplicates');
        }

        return res.body.data;
    },

    // Playlists and folders into one trash batch (#150). One transaction, so it either
    // all happened or none of it did; any refusal throws.
    deletePlaylistNodes: async (args: PymixClientArgs & { nodeIds: string[] }) => {
        const { baseUrl, nodeIds, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).deletePlaylistNodes({
            body: { node_ids: nodeIds },
        });

        if (res.status !== 200) {
            throw new Error('Failed to delete');
        }

        return res.body.data;
    },

    deleteSong: async (args: DeleteSongArgs & PymixClientArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).deleteSong({ body });

        if (res.status !== 200) {
            throw new Error('Failed to delete song');
        }

        // pymix signals per-track failure in the *body*, not the HTTP status: a failed
        // delete still returns 200 with `{ success: false, results: [...] }`. Surface
        // that as a thrown error so the caller can show which tracks failed instead of
        // a false success toast (laker-93/subbox-app#18).
        const data = res.body.data;
        if (!data.success) {
            const failed = data.results.filter((r) => !r.success);
            const detail =
                failed
                    .map((r) => r.reason)
                    .filter(Boolean)
                    .join('; ') || 'Unknown error';
            const err = new Error(
                `Failed to delete ${failed.length} of ${data.results.length} track(s): ${detail}`,
            );
            // Attach the parsed body so callers can do partial-success handling
            // (invalidate for the tracks that *did* delete).
            (err as Error & { data?: typeof data }).data = data;
            throw err;
        }

        return data;
    },

    downloadFile: async (args: DownloadFileArgs & PymixClientArgs) => {
        const { baseUrl, filename, responseType, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, responseType, signal, token }).download({
            params: { filename },
            // Unique per call: /sync/download/music.zip is the same url for every user
            // and every export, so a CDN in front of pymix can answer with a stale — or
            // another session's — file (laker-93/pymix#119, where Cloudflare cached it
            // for 4h in prod and re-downloading with different options silently returned
            // the first zip).
            query: { cache_bust: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` },
        });

        if (res.status !== 200) {
            throw new Error('Failed to download file');
        }

        return res.body.data;
    },

    emptyTrash: async (args: PymixClientArgs) => {
        const { baseUrl, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).emptyTrash();

        if (res.status !== 200) {
            throw new Error('Failed to empty the trash');
        }

        return res.body.data;
    },

    // The song table's DJ columns for up to 500 tracks (#240, pymix#269).
    getDjSummary: async (args: PymixClientArgs & { subboxIds: string[] }): Promise<DjSummary> => {
        const { baseUrl, signal, subboxIds, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).getDjSummary({
            body: { subbox_ids: subboxIds },
        });

        if (res.status !== 200) {
            throw new Error('Failed to get DJ summaries');
        }

        return res.body.data;
    },

    getLibrarySize: async (args: PymixClientArgs) => {
        const { baseUrl, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).getLibrarySize();

        if (res.status !== 200) {
            throw new Error('Failed to get library size');
        }

        return res.body.data;
    },

    // The user's playlist tree, or null when there definitely isn't one: 403 (demo),
    // 404 (a pymix without the route) and 409 `tree_not_enabled` (a user not migrated).
    // Anything else throws, so a caller can tell "no tree" from "couldn't ask" (design
    // §4.4): only the first may send a playlist delete straight to Navidrome.
    getPlaylistTree: async (args: PymixClientArgs): Promise<null | PlaylistTree> => {
        const { baseUrl, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).getPlaylistTree();

        if (res.status === 403 || res.status === 404 || res.status === 409) {
            return null;
        }

        if (res.status !== 200) {
            throw new Error('Failed to get playlist tree');
        }

        return res.body.data;
    },

    // One track's DJ data for the DJ views (#237, pymix#269), or null for a track that
    // isn't in the user's library (404).
    getTrackDj: async (args: PymixClientArgs & { subboxId: string }): Promise<DjTrack | null> => {
        const { baseUrl, signal, subboxId, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).getTrackDj({
            params: { subboxId },
        });

        if (res.status === 404) {
            return null;
        }

        if (res.status !== 200) {
            throw new Error('Failed to get DJ data');
        }

        return res.body.data;
    },

    // A track's DJ versions, newest first, each with what it changed (#241, pymix#270).
    getTrackDjHistory: async (args: PymixClientArgs & { subboxId: string }): Promise<DjHistory> => {
        const { baseUrl, signal, subboxId, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).getTrackDjHistory({
            params: { subboxId },
        });

        if (res.status !== 200) {
            throw new Error('Failed to get DJ history');
        }

        return res.body.data;
    },

    // The restorable batches, newest first (#150; the Trash screen is #152).
    getTrash: async (args: PymixClientArgs) => {
        const { baseUrl, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).getTrash();

        if (res.status !== 200) {
            throw new Error('Failed to get the trash');
        }

        return res.body.data;
    },

    // The user's own settings (#236), or null from a pymix without the route (404),
    // which the caller treats as every setting off.
    getUserSettings: async (args: PymixClientArgs): Promise<null | UserSettings> => {
        const { baseUrl, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).getUserSettings();

        if (res.status === 404) {
            return null;
        }

        if (res.status !== 200) {
            throw new Error('Failed to get settings');
        }

        return res.body.data;
    },

    import: async (args: ImportArgs & PymixClientArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).import({ body });

        if (res.status === 403) {
            throw new PymixUploadForbiddenError('import music');
        }

        if (res.status !== 200) {
            throw new Error('Failed to start import');
        }

        return res.body.data;
    },

    importProgress: async (args: ImportProgressArgs & PymixClientArgs) => {
        const { baseUrl, query, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).importProgress({ query });

        if (res.status !== 200) {
            throw new Error('Failed to get import progress');
        }

        return res.body.data;
    },

    login: async (args: LoginArgs & PymixClientArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).login({ body });

        if (res.status !== 200) {
            throw new Error('Failed to login');
        }

        return res.body.data;
    },

    matchTracks: async (args: MatchTracksArgs & PymixClientArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).matchTracks({ body });

        if (res.status === 403) {
            throw new PymixUploadForbiddenError('upload music');
        }

        if (res.status !== 200) {
            throw new Error('Failed to match tracks');
        }

        return res.body.data;
    },

    // One edit to a track's DJ data (#241, pymix#270): the track at its new version, or
    // `conflict` with the track as it now is when `expected_version` is stale (409).
    // A refused edit (422) throws with pymix's reason, which is written for the user.
    patchTrackDj: async (
        args: PymixClientArgs & { body: DjPatch; subboxId: string },
    ): Promise<DjWriteResult> => {
        const { baseUrl, body, signal, subboxId, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).patchTrackDj({
            body,
            params: { subboxId },
        });
        return djWriteResult(res, 'Failed to save the edit');
    },

    // Destroy one batch now. For playlists, the only place they leave Navidrome.
    purgeTrashBatch: async (args: PymixClientArgs & { batchId: string }) => {
        const { baseUrl, batchId, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).purgeTrashBatch({
            params: { id: batchId },
        });

        if (res.status !== 200) {
            throw new Error('Failed to purge from the trash');
        }

        return res.body.data;
    },

    rbDownload: async (args: PymixClientArgs & RbDownloadArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).rbDownload({ body });

        if (res.status !== 200) {
            throw new Error('Failed to export rekordbox');
        }

        return res.body.data;
    },

    rbImport: async (args: PymixClientArgs & RbImportArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).rbImport({ body });

        if (res.status === 403) {
            throw new PymixUploadForbiddenError('import a Rekordbox library');
        }

        if (res.status !== 200) {
            throw new Error('Failed to import rekordbox');
        }

        return res.body.data;
    },

    /**
     * Register interest in the private beta. Unauthenticated — the caller has no account,
     * which is the point.
     *
     * Distinguishes the server's two rejections rather than throwing one generic error,
     * because the form renders them differently: a 400 is the user's input (bad email)
     * and belongs inline against the field, a 429 is the per-IP cap and is not their
     * fault. Everything else is a generic failure.
     */
    requestInvite: async (args: InviteRequestArgs & PymixClientArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).inviteRequest({ body });

        if (res.status === 400) {
            throw new PymixInviteRequestError('invalid');
        }

        if (res.status === 429) {
            throw new PymixInviteRequestError('rateLimited');
        }

        if (res.status !== 200) {
            throw new PymixInviteRequestError('failed');
        }

        return res.body.data;
    },

    // Put a batch back. A playlist/folder batch restores synchronously and answers with
    // what didn't come back as it went; a track batch answers with a job to poll (#151).
    restoreTrashBatch: async (args: PymixClientArgs & { batchId: string }) => {
        const { baseUrl, batchId, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).restoreTrashBatch({
            params: { id: batchId },
        });

        if (res.status !== 200) {
            throw new Error('Failed to restore from the trash');
        }

        return res.body.data;
    },

    // Write an earlier version back as a new one (#241, pymix#270); 409 as patchTrackDj.
    revertTrackDj: async (
        args: PymixClientArgs & { expectedVersion: number; subboxId: string; toVersion: number },
    ): Promise<DjWriteResult> => {
        const { baseUrl, expectedVersion, signal, subboxId, token, toVersion } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).revertTrackDj({
            body: { expected_version: expectedVersion, to_version: toVersion },
            params: { subboxId },
        });
        return djWriteResult(res, 'Failed to revert');
    },

    seratoExport: async (args: PymixClientArgs & SeratoExportArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).seratoExport({ body });

        if (res.status !== 200) {
            throw new Error('Failed to build the Serato export');
        }

        return res.body.data;
    },

    seratoImport: async (args: PymixClientArgs & SeratoImportArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).seratoImport({ body });

        if (res.status === 403) {
            throw new PymixUploadForbiddenError('import a Serato library');
        }

        if (res.status !== 200) {
            throw new Error('Failed to import serato');
        }

        return res.body.data;
    },

    sync: async (args: PymixClientArgs & SyncArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).sync({ body });

        if (res.status !== 200) {
            throw new Error('Failed to sync');
        }

        return res.body.data;
    },

    syncPlan: async (args: PymixClientArgs & SyncPlanArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).syncPlan({ body });

        if (res.status !== 200) {
            throw new Error('Failed to get sync plan');
        }

        return res.body.data;
    },

    syncPlaylists: async (args: PymixClientArgs & SyncPlaylistsArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).syncPlaylists({ body });

        if (res.status !== 200) {
            throw new Error('Failed to sync playlists');
        }

        return res.body.data;
    },

    syncTracks: async (args: PymixClientArgs & SyncTracksArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).syncTracks({ body });

        if (res.status !== 200) {
            throw new Error('Failed to sync tracks');
        }

        return res.body.data;
    },

    // A track restore job's progress (#152).
    trashRestoreProgress: async (args: PymixClientArgs & { jobId: string }) => {
        const { baseUrl, jobId, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).trashRestoreProgress({
            query: { job_id: jobId },
        });

        if (res.status !== 200) {
            throw new Error('Failed to get restore progress');
        }

        return res.body.data;
    },

    updatePlaylistNode: async (
        args: PymixClientArgs & { body: UpdatePlaylistNodeBody; nodeId: string },
    ) => {
        const { baseUrl, body, nodeId, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).updatePlaylistNode({
            body,
            params: { id: nodeId },
        });

        if (res.status !== 200) {
            throw new Error('Failed to update playlist tree');
        }

        return res.body.data;
    },

    // Changes only the settings named, and answers with all of them. `demo` gets a 403:
    // it's one login shared by every trial visitor, so its choices stay on the device.
    updateUserSettings: async (
        args: PymixClientArgs & { body: Partial<UserSettings> },
    ): Promise<UserSettings> => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).updateUserSettings({ body });

        if (res.status !== 200) {
            throw new Error('Failed to save settings');
        }

        return res.body.data;
    },

    validateToken: async (args: PymixClientArgs & ValidateTokenArgs) => {
        const { baseUrl, query, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).validateToken({ query });

        if (res.status !== 200) {
            throw new Error('Failed to validate token');
        }

        return res.body.data;
    },

    wishlistBulkCreate: async (args: PymixClientArgs & WishlistBulkCreateArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).wishlistBulkCreate({ body });

        if (res.status !== 200) {
            throw new Error('Failed to create wishlist items');
        }

        return res.body.data;
    },

    wishlistCreate: async (args: PymixClientArgs & WishlistCreateArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).wishlistCreate({ body });

        if (res.status !== 200) {
            throw new Error('Failed to create wishlist item');
        }

        return res.body.data;
    },

    wishlistDelete: async (args: PymixClientArgs & WishlistDeleteArgs) => {
        const { baseUrl, params, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).wishlistDelete({ params });

        if (res.status !== 200) {
            throw new Error('Failed to delete wishlist item');
        }

        return res.body.data;
    },

    wishlistList: async (args: PymixClientArgs) => {
        const { baseUrl, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).wishlistList();

        if (res.status !== 200) {
            throw new Error('Failed to get wishlist');
        }

        return res.body.data;
    },

    wishlistMatchMetadata: async (args: PymixClientArgs & WishlistMatchMetadataArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).wishlistMatchMetadata({
            body,
        });

        if (res.status !== 200) {
            throw new Error('Failed to match metadata');
        }

        return res.body.data;
    },

    wishlistMatchYoutube: async (args: PymixClientArgs & WishlistMatchYoutubeArgs) => {
        const { baseUrl, params, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).wishlistMatchYoutube({
            params,
        });

        if (res.status !== 200) {
            throw new Error('Failed to match YouTube video');
        }

        return res.body.data;
    },

    wishlistParseLink: async (args: PymixClientArgs & WishlistParseLinkArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).wishlistParseLink({ body });

        if (res.status !== 200) {
            throw new Error('Failed to parse link');
        }

        return res.body.data;
    },

    wishlistSetSheet: async (args: PymixClientArgs & WishlistSetSheetArgs) => {
        const { baseUrl, body, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).wishlistSetSheet({ body });

        if (res.status !== 200) {
            throw new Error('Failed to set wishlist sheet');
        }

        return res.body.data;
    },

    wishlistSheetStatus: async (args: PymixClientArgs) => {
        const { baseUrl, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).wishlistSheetStatus();

        if (res.status !== 200) {
            throw new Error('Failed to get wishlist sheet status');
        }

        return res.body.data;
    },

    wishlistUpdate: async (args: PymixClientArgs & WishlistDeleteArgs & WishlistUpdateArgs) => {
        const { baseUrl, body, params, signal, token } = args;
        const res = await pymixApiClient({ baseUrl, signal, token }).wishlistUpdate({
            body,
            params,
        });

        if (res.status !== 200) {
            throw new Error('Failed to update wishlist item');
        }

        return res.body.data;
    },
};
